import { isNativeApp } from '../utils/platform';

// The native apps' session. The website keeps the auth server's authToken in an HttpOnly cookie, but
// in the iOS app that cookie would be third-party (the app runs on an app:// origin) and dropped. So
// the apps use the auth server's token mode instead (see server-common.ts): they send the token in a
// header and get new values back in it. It's kept in the device's secure storage (iOS Keychain,
// Android Keystore) by the session-store plugin, and in memory once loaded. Android uses the same
// mode, so both apps share one session mechanism.

const TOKEN_HEADER = 'X-Auth-Token';

let token: string | undefined;
let loading: Promise<void> | undefined;

const store = () => (window as any).cordova?.plugins?.sessionStore as {
  get(): Promise<string | null>; set(value: string): Promise<void>; clear(): Promise<void>;
} | undefined;

/** Loads the stored token (once). Auth-host requests await this before sending their headers. */
export function loadNativeSession(): Promise<void> {
  if (!isNativeApp) return Promise.resolve();
  // deviceready is sticky: a listener added after it fired runs right away. Should it never come
  // (a broken plugin), continue without a stored session instead of blocking every auth request.
  return loading ??= new Promise<void>(resolve => {
    document.addEventListener('deviceready', () => resolve(), { once: true });
    setTimeout(resolve, 5000);
  })
    .then(() => store()?.get())
    .then(value => { token = value || undefined; })
    .catch(e => console.error('Could not read the stored session', e));
}

/** Headers for auth-host requests: token mode, with the token once there is one. */
export function nativeSessionHeaders(): Record<string, string> {
  if (!isNativeApp) return {};
  return { 'X-Auth-Mode': 'token', ...(token ? { [TOKEN_HEADER]: token } : {}) };
}

/** Takes a new token (or its removal: empty value) from an auth-host response. */
export function takeNativeSession(response: Response): void {
  if (!isNativeApp) return;
  const value = response.headers.get(TOKEN_HEADER);
  if (value == null || value == (token ?? '')) return;
  token = value || undefined;
  (token ? store()?.set(token) : store()?.clear())?.catch(e => console.error('Could not store the session', e));
}

/** Forgets the session on this device (explicit logout). */
export function clearNativeSession(): void {
  if (!isNativeApp || !token) return;
  token = undefined;
  store()?.clear().catch(e => console.error('Could not clear the stored session', e));
}
